/**
 * Phase 16 — engagement surfaces (web). Mirrors mobile:
 *   <SkeletonCards/>    pulsing placeholders while agents run
 *   <DailyPickBanner/>  today's PIQ pick, tappable → chat query
 *   <WatchStrip/>       active price watches + drop alerts
 *   <RecentChips/>      "Continue planning …" from localStorage memory
 */
import { useEffect, useState } from "react";
import { Bell, BellOff, TrendingDown, Sparkles } from "lucide-react";
import {
    fetchDailyPick, listWatches, deleteWatch, loadMemory,
} from "@/lib/engagement";

const CCY = {
    INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "د.إ", SGD: "S$", JPY: "¥", THB: "฿",
};
const fmt = (amt, ccy) =>
    amt == null ? "" : `${CCY[(ccy || "USD").toUpperCase()] || ""}${Math.round(amt).toLocaleString()}`;

// ── Skeletons ────────────────────────────────────────────────────────
export function SkeletonCards({ count = 2 }) {
    return (
        <div className="space-y-3" data-testid="skeleton-cards">
            {Array.from({ length: count }).map((_, i) => (
                <div
                    key={i}
                    className="animate-pulse rounded-xl border border-[#E5E5E5] bg-white p-4"
                    data-testid={`skeleton-card-${i}`}
                >
                    <div className="h-2.5 w-2/5 rounded-full bg-[#EDEDED]" />
                    <div className="mt-3 flex gap-3">
                        <div className="h-11 flex-1 rounded-lg bg-[#F3F3F3]" />
                        <div className="h-11 w-24 rounded-lg bg-[#F3F3F3]" />
                    </div>
                    <div className="mt-3 h-2.5 w-3/5 rounded-full bg-[#EDEDED]" />
                </div>
            ))}
        </div>
    );
}

// ── Daily pick ───────────────────────────────────────────────────────
export function DailyPickBanner({ originIata, currency, onPick }) {
    const [pick, setPick] = useState(null);
    useEffect(() => {
        let alive = true;
        if (!originIata) return undefined;
        fetchDailyPick(originIata, currency).then((p) => { if (alive) setPick(p); });
        return () => { alive = false; };
    }, [originIata, currency]);

    if (!pick?.pick?.city) return null;
    const p = pick.pick;
    const price = fmt(p.flight_min, p.currency);
    return (
        <button
            type="button"
            onClick={() => onPick(`Plan a trip to ${p.city}`)}
            className="flex w-full items-center gap-2 rounded-full border border-[#E9D5FF] bg-[#FAF5FF] px-4 py-2 text-left transition hover:border-[#7C3AED]"
            data-testid="daily-pick-banner"
        >
            <Sparkles size={14} strokeWidth={2} className="shrink-0 text-[#7C3AED]" />
            <span className="truncate text-[12.5px] font-medium text-[#0A0A0A]">
                <span className="font-semibold tracking-wide text-[#7C3AED]">Today's pick&nbsp;&nbsp;</span>
                {p.city}
                {p.vibe && <span className="text-[10px] tracking-wider text-[#A3A3A3]">&nbsp;&nbsp;{String(p.vibe).toUpperCase()}</span>}
                {price && <span className="font-semibold text-[#2563EB]">&nbsp;&nbsp;✈ from {price}</span>}
            </span>
        </button>
    );
}

// ── Watches ──────────────────────────────────────────────────────────
export function WatchStrip({ onPick }) {
    const [watches, setWatches] = useState([]);
    useEffect(() => {
        let alive = true;
        listWatches()
            .then((r) => { if (alive) setWatches(r.watches || []); })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    if (!watches.length) return null;
    return (
        <div className="space-y-1.5" data-testid="watch-strip">
            <div className="text-[10px] font-semibold tracking-[0.2em] text-[#A3A3A3]">WATCHING</div>
            {watches.map((w) => {
                const cur = w.last_price?.amount;
                return (
                    <div
                        key={w.id}
                        className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${
                            w.is_drop ? "border-[#BBF7D0] bg-[#F0FDF4]" : "border-[#E5E5E5] bg-white"
                        }`}
                        data-testid={`watch-${w.id}`}
                    >
                        {w.is_drop
                            ? <TrendingDown size={13} strokeWidth={2.25} className="shrink-0 text-[#16A34A]" />
                            : <Bell size={12} strokeWidth={2} className="shrink-0 text-[#737373]" />}
                        <button
                            type="button"
                            className="flex-1 truncate text-left text-[12px] font-medium text-[#0A0A0A]"
                            onClick={() => onPick(
                                `Flights from ${w.origin} to ${w.destination}` +
                                (w.depart_date ? ` on ${w.depart_date}` : ""),
                            )}
                            data-testid={`watch-open-${w.id}`}
                        >
                            {w.label}{cur ? ` · ${fmt(cur, w.last_price?.currency)}` : ""}
                            {w.is_drop && w.delta_pct != null && (
                                <span className="font-semibold text-[#16A34A]">&nbsp;&nbsp;↓ {Math.abs(w.delta_pct)}%</span>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                deleteWatch(w.id).catch(() => {});
                                setWatches((prev) => prev.filter((x) => x.id !== w.id));
                            }}
                            data-testid={`watch-remove-${w.id}`}
                            aria-label="Stop watching"
                        >
                            <BellOff size={12} strokeWidth={2} className="text-[#A3A3A3] transition hover:text-[#0A0A0A]" />
                        </button>
                    </div>
                );
            })}
        </div>
    );
}

// ── Recent destinations ──────────────────────────────────────────────
export function RecentChips({ onPick }) {
    const [recent, setRecent] = useState([]);
    useEffect(() => { setRecent(loadMemory().recent_destinations); }, []);
    if (!recent.length) return null;
    return (
        <div className="flex flex-wrap gap-1.5" data-testid="recent-chips">
            {recent.slice(0, 4).map((d) => (
                <button
                    key={d}
                    type="button"
                    onClick={() => onPick(`Continue planning my trip to ${d}`)}
                    className="rounded-full border border-[#E5E5E5] bg-white px-3 py-1 text-[11.5px] font-medium text-[#0A0A0A] transition hover:border-[#0A0A0A]"
                    data-testid={`recent-chip-${d.replace(/\s+/g, "-").toLowerCase()}`}
                >
                    ↺ {d}
                </button>
            ))}
        </div>
    );
}
